import axios, { AxiosHeaders, AxiosRequestConfig, AxiosResponse, type InternalAxiosRequestConfig } from 'axios'
import { queryClient } from 'components/api/queryClient'
import type { QueryKey } from '@tanstack/react-query'

interface RequestConfig<Data> extends AxiosRequestConfig<Data> {
  url: string
  cacheOptions?: {
    enabled?: boolean // default: true for GET
    staleTime?: number // optional per-call override
    gcTime?: number // optional per-call override
  }
}

export interface ApiResponse<T> extends AxiosResponse<T> {}

export const API_ERROR_MESSAGE = 'API Client Error'

/**
 * Creates a CSRF Header that is independent of the underlying API Client
 * @param headerObj The axios header object.
 * @param htmlDocument The document object.
 * @returns Updated header object with the CSRF token header added
 */
export const addCsrfHeader = (headerObj: RequestConfig<any>['headers'] = {}, htmlDocument: Document = document): RequestConfig<any>['headers'] => {
  const tokenElement = htmlDocument.querySelector('[name=csrf-token]')
  if (tokenElement !== null) {
    headerObj['X-CSRF-TOKEN'] = (tokenElement as HTMLMetaElement).content
  }
  return headerObj
}

/** Stable stringify so {a:1,b:2} and {b:2,a:1} produce the same cache key */
const stableStringify = (obj: unknown): string => {
  if (obj === null || typeof obj !== 'object') return JSON.stringify(obj)
  if (Array.isArray(obj)) return `[${obj.map(stableStringify).join(',')}]`
  const entries = Object.entries(obj as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b))
  return `{${entries.map(([k, v]) => `"${k}":${stableStringify(v)}`).join(',')}}`
}

/** Remove undefined and empty-string params (avoid cache-key drift) */
const normalizeParams = <T extends Record<string, unknown>>(obj: T): Partial<T> => {
  const out: Partial<T> = {}
  for (const [k, v] of Object.entries(obj) as Array<[keyof T, T[keyof T]]>) {
    if (v === undefined) continue
    if (v === '') continue
    out[k] = v
  }
  return out
}

/** Only these headers affect the cache key */
const pickVaryHeaders = (
  hdr: Record<string, string | number | boolean | undefined>
): Record<string, string | number | boolean> => {
  const vary: Record<string, string | number | boolean> = {}
  ;['Authorization', 'X-ORG', 'X-TENANT', 'Accept-Language'].forEach((h) => {
    const v = hdr[h] ?? hdr[h.toLowerCase()]
    if (v != null) vary[h] = v
  })
  return vary
}

type QueryKeyRequest = Pick<AxiosRequestConfig, 'headers' | 'method' | 'params' | 'url'>

/** Derive a cache key from method + URL + params (+ a few headers if present) */
const buildQueryKey = (request: QueryKeyRequest): QueryKey => {
  const method = (request.method ?? 'get').toUpperCase()
  const url = request.url ?? ''

  const params = normalizeParams((request.params ?? {}) as Record<string, unknown>)
  const varyHeaders = pickVaryHeaders(
    (request.headers ?? {}) as Record<string, string | number | boolean | undefined>
  )

  return ['API', method, url, stableStringify(params), stableStringify(varyHeaders)]
}

export interface AxiosResponseWithQueryKey<Response> extends AxiosResponse<Response> {
  queryKey?: QueryKey
}

/**
 * GET request
 * @param request The axios request config.
 * @returns A promise resolving to the axios response.
 */
const get = async <Data = unknown, Response = unknown>(request: RequestConfig<Data>): Promise<AxiosResponseWithQueryKey<Response>> => {
  // cache GETs by default via React Query
  const cacheEnabled = request.cacheOptions?.enabled ?? true
  if (!cacheEnabled) {
    return await httpFetch({ method: 'get', ...request })
  }

  const queryKey = buildQueryKey({ method: 'get', ...request })

  // NOTE: At the *defaults* level, TanStack Query allows staleTime/gcTime to be:
  //   - a number,
  //   - a function (StaleTimeFunction),
  //   - or the string "static".
  // Here we only want to forward a concrete number to fetchQuery. Functions/"static"
  // are ignored so React Query can resolve them internally. This also avoids a TS
  // mismatch when our fetchQuery generics expect a numeric staleTime.
  // (That's why we use the typeof === 'number' guard rather than `defaults?.staleTime` directly.)
  const { queries: defaults = {} } = queryClient.getDefaultOptions()

  const staleTime: number | undefined =
    request.cacheOptions?.staleTime ??
    (typeof defaults.staleTime === 'number' ? defaults.staleTime : undefined)

  const gcTime: number | undefined =
    request.cacheOptions?.gcTime ??
    (typeof defaults.gcTime === 'number' ? defaults.gcTime : undefined)

  const data = await queryClient.fetchQuery<Response>({
    queryKey,
    queryFn: async () => {
      const res = await httpFetch<Data, Response>({ method: 'get', ...request })
      return res.data
    },
    staleTime,
    gcTime
  })

  // AxiosResponse expects config: InternalAxiosRequestConfig with AxiosHeaders
  const hdrInput = request.headers as AxiosHeaders | undefined
  const hdrs = hdrInput instanceof AxiosHeaders ? hdrInput : new AxiosHeaders(hdrInput)
  const configForResponse: InternalAxiosRequestConfig = {
    ...(request as AxiosRequestConfig<Data>),
    headers: hdrs
  }

  return {
    data,
    status: 200,
    statusText: 'OK',
    headers: {},
    config: configForResponse,
    queryKey
  }
}

/**
 * DELETE request
 * @param request The axios request config.
 * @returns A promise resolving to the axios response.
 */
const httpDelete = async <Data = unknown, Response = unknown>(request: RequestConfig<Data>): Promise<AxiosResponse<Response>> => {
  // httpDelete because `delete` cannot be used as a variable name.
  return await httpFetch({ method: 'delete', ...request })
}

/**
 * DELETE request with CSRF Token added to the header
 * @param request The axios request config.
 * @returns A promise resolving to the axios response.
 */
const deleteWithCsrf = async <Data = unknown, Response = unknown>(request: RequestConfig<Data>): Promise<AxiosResponse<Response>> => {
  request.headers = addCsrfHeader(request.headers, document)
  return await httpDelete(request)
}

/**
 * PATCH request
 * @param request The axios request config.
 * @returns A promise resolving to the axios response.
 */
const patch = async <Data = unknown, Response = unknown>(request: RequestConfig<Data>): Promise<AxiosResponse<Response>> => {
  return await httpFetch({ method: 'patch', ...request })
}

/**
 * PATCH request with CSRF Token added to the header
 * @param request The axios request config.
 * @returns A promise resolving to the axios response.
 */
const patchWithCsrf = async <Data = unknown, Response = unknown>(request: RequestConfig<Data>): Promise<AxiosResponse<Response>> => {
  request.headers = addCsrfHeader(request.headers, document)
  return await patch(request)
}

/**
 * POST request
 * @param request The axios request config.
 * @returns A promise resolving to the axios response.
 */
const post = async <Data = unknown, Response = unknown>(request: RequestConfig<Data>): Promise<AxiosResponse<Response>> => {
  return await httpFetch({ method: 'post', ...request })
}

/**
 * POST request with CSRF Token added to the header
 * @param request The axios request config.
 * @returns A promise resolving to the axios response.
 */
const postWithCsrf = async <Data = unknown, Response = unknown>(request: RequestConfig<Data>): Promise<AxiosResponse<Response>> => {
  request.headers = addCsrfHeader(request.headers, document)
  return await post(request)
}

/**
 * PUT request
 * @param request
 * @returns A promise resolving to the axios response.
 */
const put = async <Data = unknown, Response = unknown>(request: RequestConfig<Data>): Promise<AxiosResponse<Response>> => {
  return await httpFetch({ method: 'put', ...request })
}

/**
 * PUT request with CSRF Token added to the header
 * @param request The axios request config.
 * @returns A promise resolving to the axios response.
 */
const putWithCsrf = async <Data = unknown, Response = unknown>(request: RequestConfig<Data>): Promise<AxiosResponse<Response>> => {
  request.headers = addCsrfHeader(request.headers, document)
  return await put(request)
}

const httpFetch = async <Data = unknown, Response = unknown>(request: AxiosRequestConfig<Data>): Promise<AxiosResponse<Response>> => {
  try {
    return await axios(request)
  } catch (e) {
    // Provide some useful information to help debug the error/unhandled rejection
    // even if the error is unhandled or the stack trace is not useful (for example a bad source map)
    console.error(`${API_ERROR_MESSAGE}: ${request?.method?.toUpperCase() ?? ''} ${request?.url ?? ''}`)
    throw e
  }
}

// Create an API Client that abstracts the client implementation from the rest of our code
export const api = {
  delete: httpDelete,
  deleteWithCsrf,
  get,
  patch,
  patchWithCsrf,
  post,
  postWithCsrf,
  put,
  putWithCsrf
}
